import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { aiDraftsAPI, companiesAPI, keyResultsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Check, 
  X, 
  Edit3,
  Clock,
  Target,
  RefreshCw,
  Building2,
  ListChecks,
  Package,
  TrendingUp
} from 'lucide-react';

const AITaskDraftsPage = () => {
  const { isExecutive } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    effort: 'M',
    priority: 'medium',
    definition_of_done: ''
  });

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const [draftsRes, companiesRes, krsRes] = await Promise.all([
        aiDraftsAPI.getAll(filter !== 'all' ? filter : undefined),
        companiesAPI.getAll(),
        keyResultsAPI.getAll({})
      ]);
      setDrafts(draftsRes.data);
      setCompanies(companiesRes.data);
      setKeyResults(krsRes.data);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      toast.error('Error al cargar borradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.code || 'N/A';
  };

  const getKRTitle = (krId) => {
    const kr = keyResults.find(k => k.kr_id === krId);
    return kr?.title || 'N/A';
  };

  const handleApprove = async (draftId) => {
    try {
      await aiDraftsAPI.update(draftId, { status: 'approved' });
      toast.success('Tarea aprobada y creada');
      fetchDrafts();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleReject = async (draftId) => {
    try {
      await aiDraftsAPI.update(draftId, { status: 'rejected' });
      toast.success('Borrador rechazado');
      fetchDrafts();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const handleEdit = (draft) => {
    setEditingDraft(draft);
    setEditForm({
      title: draft.title,
      description: draft.description,
      effort: draft.effort,
      priority: draft.priority,
      definition_of_done: draft.definition_of_done
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDraft) return;
    try {
      await aiDraftsAPI.update(editingDraft.draft_id, { ...editForm, status: 'edited' });
      toast.success('Borrador actualizado');
      setShowEditDialog(false);
      fetchDrafts();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleApproveEdited = async () => {
    if (!editingDraft) return;
    try {
      await aiDraftsAPI.update(editingDraft.draft_id, { ...editForm, status: 'approved' });
      toast.success('Tarea aprobada y creada');
      setShowEditDialog(false);
      fetchDrafts();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const getAgentLabel = (type) => {
    const labels = {
      planner: 'Planner',
      daily_focus: 'Daily Focus',
      blocker_analyst: 'Blocker Analyst',
      delegation_advisor: 'Delegation Advisor'
    };
    return labels[type] || type;
  };

  const priorityColors = {
    high: 'bg-red-50 text-red-700',
    medium: 'bg-amber-50 text-amber-700',
    low: 'bg-gray-100 text-gray-600'
  };

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
    edited: 'bg-blue-50 text-blue-700'
  };

  if (!isExecutive) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-4" />
        <h2 className="text-base font-medium text-gray-900 mb-2">Acceso Restringido</h2>
        <p className="text-sm text-gray-500">Solo Owner y Executive pueden ver los borradores de IA.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            AI Task Drafts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Borradores de tareas generados por IA
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 bg-white border-gray-200 text-sm">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobados</SelectItem>
              <SelectItem value="rejected">Rechazados</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            className="border-gray-200"
            onClick={fetchDrafts}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Drafts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
        </div>
      ) : drafts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-4" />
          <h2 className="text-base font-medium text-gray-900 mb-2">Sin borradores</h2>
          <p className="text-sm text-gray-500">
            Los borradores aparecerán cuando generes tareas con IA desde el Scorecard.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {drafts.map((draft) => (
            <div 
              key={draft.draft_id}
              className="bg-white border border-gray-200 rounded-lg p-5"
              data-testid={`draft-${draft.draft_id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="tag tag-blue text-xs">{getAgentLabel(draft.agent_type)}</span>
                  <span className={`tag text-xs ${statusColors[draft.status]}`}>{draft.status}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(draft.created_at).toLocaleDateString('es-ES')}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-base font-medium text-gray-900 mb-2">{draft.title}</h3>
              
              {/* Company and KR */}
              <div className="flex items-center gap-3 mb-3 text-sm">
                <span className="flex items-center gap-1.5 text-blue-600">
                  <Building2 className="w-3.5 h-3.5" />
                  {getCompanyName(draft.company_id)}
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-gray-500 truncate" title={getKRTitle(draft.kr_id)}>
                  {getKRTitle(draft.kr_id)}
                </span>
              </div>
              
              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">{draft.description}</p>

              {/* Steps - How to complete */}
              {draft.steps && draft.steps.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wider">Cómo completar</span>
                  </div>
                  <ol className="space-y-1.5">
                    {draft.steps.map((step, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Resources Needed */}
              {draft.resources_needed && draft.resources_needed.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700 uppercase tracking-wider">Recursos necesarios</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {draft.resources_needed.map((resource, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-white border border-amber-200 rounded text-amber-700">
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="tag tag-default text-xs flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {draft.effort}
                </span>
                <span className={`tag text-xs ${priorityColors[draft.priority]}`}>
                  {draft.priority}
                </span>
                {draft.suggested_date && (
                  <span className="tag tag-default text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(draft.suggested_date).toLocaleDateString('es-ES')}
                  </span>
                )}
                {draft.impact_on_kr && (
                  <span className="tag tag-green text-xs flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {draft.impact_on_kr}
                  </span>
                )}
              </div>

              {/* Definition of Done */}
              {draft.definition_of_done && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 mb-4">
                  <span className="text-xs font-medium text-emerald-700 uppercase tracking-wider block mb-1">Definition of Done</span>
                  <p className="text-sm text-emerald-800">{draft.definition_of_done}</p>
                </div>
              )}

              {/* Actions */}
              {draft.status === 'pending' && (
                <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                  <Button
                    onClick={() => handleApprove(draft.draft_id)}
                    className="bg-gray-900 hover:bg-gray-800 text-white text-sm"
                  >
                    <Check className="w-4 h-4 mr-1.5" />
                    Aprobar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleEdit(draft)}
                    className="border-gray-200 text-sm"
                  >
                    <Edit3 className="w-4 h-4 mr-1.5" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleReject(draft.draft_id)}
                    className="border-gray-200 text-red-600 hover:text-red-700 hover:bg-red-50 text-sm"
                  >
                    <X className="w-4 h-4 mr-1.5" />
                    Rechazar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle>Editar Borrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Título"
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              className="border-gray-200"
            />
            <Textarea
              placeholder="Descripción"
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              className="border-gray-200"
            />
            <div className="grid grid-cols-2 gap-4">
              <Select 
                value={editForm.effort} 
                onValueChange={(v) => setEditForm(prev => ({ ...prev, effort: v }))}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder="Esfuerzo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">S (Pequeño)</SelectItem>
                  <SelectItem value="M">M (Mediano)</SelectItem>
                  <SelectItem value="L">L (Grande)</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={editForm.priority} 
                onValueChange={(v) => setEditForm(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder="Prioridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="low">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              placeholder="Definition of Done"
              value={editForm.definition_of_done}
              onChange={(e) => setEditForm(prev => ({ ...prev, definition_of_done: e.target.value }))}
              className="border-gray-200"
            />
            <div className="flex gap-2 pt-2">
              <Button onClick={handleApproveEdited} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white">
                <Check className="w-4 h-4 mr-1.5" />
                Guardar y Aprobar
              </Button>
              <Button onClick={handleSaveEdit} variant="outline" className="flex-1 border-gray-200">
                Solo Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AITaskDraftsPage;
